import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Eraser } from 'lucide-react';
import apiClient from '../services/apiClient';
import CardListeningControl from './CardListeningControl';
import PointBonusTip, { POINT_BONUS_FEEDBACK_MS } from './PointBonusTip';
import LearningSessionHeader, { LearningImportButton } from './LearningSessionHeader';
import RepeatPracticeCard from './RepeatPracticeCard';
import LearningCardProgress, { RepeatActionButton } from './LearningCardProgress';
import ReviewLevelUpDialog from './ReviewLevelUpDialog';
import WritingDrawingCanvas from './WritingDrawingCanvas';
import CelebrationPopup from './CelebrationPopup';
import useWritingTimer from '../hooks/useWritingTimer';
import useLearningCoaching from '../hooks/useLearningCoaching';
import LearningCoach from './LearningCoach';
import useRepeatInputLock from '../hooks/useRepeatInputLock';
import { writingCelebrationSnapshot, writingCelebrations } from '../services/WritingCelebrations';
import MarkdownText from './MarkdownText';
import { playPracticeSound, setPracticeSoundEnabled } from '../services/soundEffects';
import { t } from '../i18n';
import './NpcEncounterOverlay.css';
import './FlashCard.css';
import './WritingLearningPage.css';
import './WritingPracticePage.css';
import './WritingHint.css';

const w = (key, values = {}) => t(`writingExam.${key}`, undefined, values);

export default function WritingPracticePage() {
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const { scenes: coachingScenes } = useLearningCoaching('writing');
  const [answer, setAnswer] = useState('');
  const [training, setTraining] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [clearDrawing, setClearDrawing] = useState(0);
  const [hintVisible, setHintVisible] = useState(false);
  const [celebrations, setCelebrations] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [pointFeedback, setPointFeedback] = useState(null);
  const [saving, setSaving] = useState(null);
  const [saveState, setSaveState] = useState(null);
  const { locked: repeatLocked, begin: beginRepeatLock, finish: finishRepeatLock } = useRepeatInputLock();
  const controlsBusy = busy || repeatLocked;
  const startingSnapshot = useRef(null);
  const examRef = useRef(exam);
  examRef.current = exam;
  const alive = useRef(false);
  const inFlight = useRef(false);
  const initialRequest = useRef(null);
  const inputRef = useRef(null);
  const repeatControlsRef = useRef(null);
  const paused = Boolean(saving) || celebrations.length > 0;
  const { elapsedSeconds, flushTime } = useWritingTimer(exam?.id, Boolean(exam) && !completed, paused);
  const showResult = useCallback(next => {
    const current = next?.id ? next : examRef.current;
    if (!current?.id) return;
    navigate(`/level-up/result/${encodeURIComponent(current.id)}?batch=${current.batchNumber || 1}`, { replace: true });
  }, [navigate]);
  const adopt = useCallback(next => {
    if (!alive.current) return;
    if (next.complete) { showResult(next); return; }
    setExam(next);
    setAnswer(next.item?.answer || '');
    setTraining((next.item?.repeats || 0) > 0);
  }, [showResult]);
  // Start is idempotent on the server. Retain the promise across StrictMode's
  // effect replay; a direct URL or refresh resumes the stored daily attempt.
  useEffect(() => {
    alive.current = true;
    if (!startingSnapshot.current) startingSnapshot.current = writingCelebrationSnapshot();
    if (!initialRequest.current) initialRequest.current = apiClient.startWriting();
    initialRequest.current.then(adopt).catch(err => { if (alive.current) setError(err.message || w('failed')); })
      .finally(() => { if (alive.current) setLoading(false); });
    return () => { alive.current = false; };
  }, [adopt]);
  const item = exam?.item;
  const hasResult = Boolean(item?.result);
  // Audio uses the model answer, but its record-backed endpoint is available only after grading.
  // A new control on publication starts in loading state and waits for metadata before autoplay.
  const audioCardId = hasResult ? `writing~${item.id}` : null;
  useEffect(() => {
    if (!item?.id || !hasResult) { setSaveState(null); return undefined; }
    let cancelled = false;
    setSaveState({ id: item.id, loading: true, saved: false });
    apiClient.getWritingRecord(item.id).then(record => {
      if (!cancelled) setSaveState({ id: item.id, loading: false, saved: record.saved, record });
    }).catch(err => {
      if (!cancelled) {
        setSaveState({ id: item.id, loading: false, saved: false });
        setError(err.message || w('failed'));
      }
    });
    return () => { cancelled = true; };
  }, [item?.id, hasResult]);
  useEffect(() => { setHintVisible(false); }, [item?.id]);
  useEffect(() => { setPointFeedback(null); }, [item?.id]);
  useEffect(() => {
    if (!pointFeedback) return undefined;
    const timer = setTimeout(() => setPointFeedback(null), POINT_BONUS_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [pointFeedback]);
  const phase = !item?.result ? 'question' : training ? 'repeat' : 'answer';
  useEffect(() => { if (phase === 'question' && item?.status === 'asked') inputRef.current?.focus(); }, [item?.id, item?.status, phase]);

  const run = async callback => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try { await callback(); }
    catch (err) { if (alive.current) setError(err.message || w('failed')); }
    finally { inFlight.current = false; if (alive.current) setBusy(false); }
  };
  const action = (name, payload = {}) => run(async () => {
    await flushTime();
    if (name === 'submit') {
      const submitted = payload.answer.trim();
      setAnswer(submitted);
      setExam(current => ({ ...current, item: { ...current.item, answer: submitted, status: 'submitted' } }));
    }
    const next = await apiClient.writingAction(exam.id, item.id, name, payload);
    if (!alive.current) return;
    if (name === 'repeat') {
      void playPracticeSound('repeat', { repeatIndex: payload.count, targetRepeats: 8, recoverOnGesture: true });
      setPointFeedback({ id: `${item.id}:repeat:${payload.count}`, label: t('practiceUi.encounter.repeatBonus'), points: 1 });
    }
    if (next.complete) {
      setCompleted(true);
      setExam(current => ({ ...current, scoreGained: next.scoreGained ?? current.scoreGained,
        item: name === 'repeat' ? { ...current.item, repeats: payload.count } : current.item }));
      let queue;
      try { queue = await writingCelebrations(await startingSnapshot.current); }
      catch { queue = []; } // Optional celebrations must never block the saved results.
      if (!alive.current) return;
      if (queue.length) setCelebrations(queue);
      else showResult(next);
      return;
    }
    adopt(next);
  });
  const finishVisit = () => run(async () => {
    await flushTime();
    if (!alive.current) return;
    if (examRef.current?.id) showResult();
    else navigate('/level-up', { replace: true });
  });
  const retryStart = () => run(async () => adopt(await apiClient.startWriting()));
  const repeat = () => {
    if (inFlight.current || completed || paused || !beginRepeatLock()) return;
    void action('repeat', { count: item.repeats + 1 }).finally(finishRepeatLock);
  };
  const submitAnswer = () => {
    if (!busy && answer.trim()) action('submit', { answer });
  };
  const handleAnswerKeyDown = event => {
    // IME confirmation (including Safari's legacy 229 event) is not submission.
    if (event.key !== 'Enter' || event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    if (!event.repeat && item.status === 'asked') submitAnswer();
  };
  const openImport = () => run(async () => {
    const record = saveState?.record || await apiClient.getWritingRecord(item.id);
    if (!alive.current) return;
    setSaveState({ id: item.id, loading: false, saved: record.saved, record });
    if (!record.saved) setSaving(record);
  });
  const saved = saveState?.id === item?.id && saveState?.saved;
  const saveLoading = saveState?.id !== item?.id || saveState?.loading;
  const currentPoints = exam?.scoreGained ?? (hasResult ? Math.floor(item.result.score / 2) + item.repeats : 0);
  const savingCard = useMemo(() => saving ? { id: saving.id, front: saving.Q, back: saving.A, cardType: 'normal', level: 1 } : null, [saving]);

  return <section className="writing-page writing-practice-page" aria-label={w('exam')}>
    <LearningSessionHeader title={<h1>{w('title')}</h1>} elapsedSeconds={elapsedSeconds}
      exitLabel={t('interface.practice.end')} exitTitle={w('later')} onExit={finishVisit} exitDisabled={controlsBusy || paused || completed}
      aria-hidden={paused || undefined} inert={paused ? '' : undefined}
      leftTools={item && <CardListeningControl key={audioCardId || item.id} cardId={audioCardId} isRevealed={hasResult} isAnswerVisible={hasResult}
        controlMode={phase === 'question' ? 'toggle' : 'playback'} interactionDisabled={repeatLocked} onAutoplayEnabledChange={setPracticeSoundEnabled} />}
      rightTools={phase === 'repeat' && <LearningImportButton imported={saved}
        disabled={controlsBusy || completed || paused || saveLoading} onClick={openImport} />} />
    {error && <div role="alert" className="writing-error">{error}{!item && <button type="button" disabled={busy} onClick={retryStart}>{w('retry')}</button>}</div>}
    {loading ? <p role="status">{w('loading')}</p> : item && <div className="writing-practice-body" aria-busy={controlsBusy} aria-hidden={paused || undefined} inert={paused ? '' : undefined}>
      <LearningCoach className="writing-practice-coach" scenes={coachingScenes}
        trigger={phase === 'question' ? `writing.question.${Number.isInteger(exam?.index) ? exam.index + 1 : 1}` : `writing.${phase}`}
        visitKey={item.id} variables={{ feedback: item.result?.feedback, currentIndex: (exam.index || 0) + 1, totalCount: exam.total }} />
      <div key={`${item.id}:${phase}`} className={`writing-practice-phase is-${phase}`}>
        {phase === 'question' ? <form onSubmit={event => { event.preventDefault(); submitAnswer(); }}>
          <div className="npc-encounter-card npc-card-mode-question writing-practice-card"><div className="npc-card-face"><MarkdownText text={item.Q} /><div className="writing-front-memo"><button type="button" className={`writing-expression-toggle${hintVisible ? '' : ' writing-faint-hint'}`} aria-pressed={hintVisible} onClick={() => setHintVisible(value => !value)}><strong>{item.expression}</strong></button></div></div></div>
          <label htmlFor="writing-answer">{w('englishAnswer')}</label>
          <textarea id="writing-answer" ref={inputRef} rows={2} required maxLength={1000} value={answer} disabled={busy || item.status === 'submitted'} onChange={event => setAnswer(event.target.value)} onKeyDown={handleAnswerKeyDown} enterKeyHint="send" autoCapitalize="sentences" lang="en" spellCheck="false" />
          <div className="writing-exam-actions">
            <button type="button" disabled={busy || item.status !== 'asked'} onClick={() => action('pass')}>{w('pass')}</button>
            <button className="writing-primary" type="submit" disabled={busy || !answer.trim()}>{busy ? w('grading') : item.status === 'submitted' ? w('retryGrade') : w('submit')} <span className="writing-button-step">{w('stepPosition', { step: 1 })}</span></button></div>
        </form> : phase === 'answer' ? <div className="writing-result">
          <div className="writing-drawing-tools"><span><Pencil size={16} aria-hidden="true" />{w('drawingHint')}</span><button type="button" onClick={() => setClearDrawing(value => value + 1)} aria-label={w('clearDrawing')} title={w('clearDrawing')}><Eraser size={18} aria-hidden="true" /></button></div>
          <div className="npc-encounter-card npc-card-mode-answer is-revealed writing-practice-card writing-answer-card" role="group" aria-label={w('answerCard')}>
            <span className="writing-earned-points" aria-label={w('earnedPoints', { points: Math.floor(item.result.score / 2) })}>+{Math.floor(item.result.score / 2)} pt</span>
            <div className="npc-card-face" aria-label={w('question')}><MarkdownText text={item.Q} /></div>
            <div className="npc-card-face npc-card-back writing-reference" lang="en" aria-label={w('reference')}><MarkdownText text={item.A} /></div>
            <div className="npc-card-face npc-card-back writing-student-answer" lang="en" aria-label={w('answer')}><MarkdownText text={item.answer} /><span className={`writing-score writing-answer-score${item.result.score === 100 ? ' is-perfect' : ''}`} aria-label={w('scoreLabel', { score: item.result.score })}><strong>{item.result.score}</strong></span></div>
            <WritingDrawingCanvas label={w('drawingHint')} clearToken={clearDrawing} />
          </div>
          <p className="writing-score-breakdown">{w('breakdown', item.result)}</p>
          <button className="writing-primary" type="button" onClick={() => setTraining(true)}>{w('practiceEight')} <span className="writing-button-step">{w('stepPosition', { step: 2 })}</span></button>
        </div> : <div className="writing-training" ref={repeatControlsRef}>
          <div className="npc-encounter-card-wrap writing-repeat-card-wrap">
          <RepeatPracticeCard question={item.Q} answer={item.A} onRepeat={repeat} disabled={controlsBusy || completed || paused}
            repeatCount={item.repeats} repeatLabel={w('repeat', { count: item.repeats })} />
          {pointFeedback && <div className="npc-point-feedback" aria-live="polite">
            <PointBonusTip bonuses={[pointFeedback]} eventId={pointFeedback.id} />
          </div>}
          </div>
          <LearningCardProgress count={item.repeats} points={currentPoints} ariaLabel={w('step3')} />
          <RepeatActionButton disabled={controlsBusy || completed || paused} onClick={repeat} label={w('step3')} stepLabel={w('stepPosition', { step: 3 })} />
        </div>}
      </div>
    </div>}
    {saving && <ReviewLevelUpDialog writingRecord={saving} card={savingCard}
      onClose={() => {
        setSaving(null);
        requestAnimationFrame(() => repeatControlsRef.current?.querySelector('button:not(:disabled)')?.focus());
      }} onCreated={() => setSaveState({ id: saving.id, loading: false, saved: true, record: { ...saving, saved: true } })} />}
    <CelebrationPopup key={celebrations[0]?.kind || 'none'} {...celebrations[0]} isVisible={celebrations.length > 0} onClose={() => {
      if (celebrations.length > 1) setCelebrations(current => current.slice(1));
      else showResult();
    }} />
  </section>;
}
